import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  SectionCard,
  FieldLabel,
  InfoRow,
  StatusBadge,
  UploadBox,
  WarningBanner,
  ApproverRoleBadge,
  EmptyState,
  useConfirm,
} from "./shared/components";
import {
  getNoDuesTasks,
  getFinanceLoans,
  submitFinanceClearance,
  formatINR,
  NoDuesTask,
  FinanceLoanItem,
} from "./services/eSeparation.service";

const FinanceClearance: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<NoDuesTask[]>([]);
  const [taskId, setTaskId] = useState("");
  const [loans, setLoans] = useState<FinanceLoanItem[]>([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [status, setStatus] = useState<"Cleared" | "Dues Found" | "">("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const load = () => {
    getNoDuesTasks().then((res) =>
      setTasks(
        (res.data || []).filter(
          (t) =>
            (t.dept_name || "").toLowerCase().includes("finance") ||
            (t.department || "").toLowerCase().includes("finance")
        )
      )
    );
  };
  useEffect(load, []);

  const task = useMemo(() => tasks.find((t) => t.task_id === taskId) || null, [tasks, taskId]);

  useEffect(() => {
    if (!task) {
      setLoans([]);
      return;
    }
    setLoadingLoans(true);
    getFinanceLoans(task.emp_no).then((res) => {
      setLoans(res.data || []);
      setLoadingLoans(false);
    });
    setRemarks(task.remarks || "");
  }, [task]);

  const totalRecovery = useMemo(
    () => loans.reduce((sum, l) => sum + (Number(l.recovery_required) || 0), 0),
    [loans]
  );

  useEffect(() => {
    if (totalRecovery > 0) setStatus("Dues Found");
    else setStatus((prev) => (prev === "Dues Found" ? "Cleared" : prev));
  }, [totalRecovery]);

  const setRecovery = (idx: number, val: string) =>
    setLoans((prev) => prev.map((l, i) => (i === idx ? { ...l, recovery_required: Number(val) || 0 } : l)));
  const setLoanRemark = (idx: number, val: string) =>
    setLoans((prev) => prev.map((l, i) => (i === idx ? { ...l, remarks: val } : l)));

  const handleSubmit = async () => {
    if (!task) return toast.warning("Please select a task first.");
    if (!status) return toast.warning("Please select a clearance status.");
    if (status === "Dues Found" && !remarks.trim())
      return toast.warning("Please enter remarks explaining the pending dues.");
    if (!(await confirm(`Submit Finance Clearance as "${status}" with recovery of ${formatINR(totalRecovery)}?`, { title: "Confirm Submission", confirmLabel: "Confirm", tone: status === "Dues Found" ? "warning" : "success" }))) return;
    setSubmitting(true);
    const res = await submitFinanceClearance({
      nd_case_id: task.nd_case_id,
      emp_no: task.emp_no,
      loans,
      total_recovery: totalRecovery,
      remarks,
      action: status,
    });
    setSubmitting(false);
    if (res.success) {
      toast.success("Finance clearance has been submitted.");
      setTaskId("");
      setStatus("");
      setRemarks("");
      load();
    } else {
      toast.error(res.message || "Submission failed.");
    }
  };

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-0">
        <PageHeader
          title="Finance Clearance"
          subtitle="Verify outstanding loans, advances & recovery dues"
          right={<ApproverRoleBadge role="Finance" />}
          onExit={() => navigate(-1)}
        />

        {/* Selector */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body">
            <div className="d-flex gap-2 align-items-end">
              <div className="flex-grow-1">
                <FieldLabel label="Select Pending Finance Clearance Case" required />
                <select className="form-select" value={taskId} onChange={(e) => setTaskId(e.target.value)}>
                  <option value="">Choose a pending case...</option>
                  {tasks.map((t) => (
                    <option key={t.task_id} value={t.task_id}>
                      {t.emp_name} ({t.emp_no})
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn btn-outline-secondary" onClick={load}>
                <i className="ti ti-refresh" />
              </button>
            </div>
          </div>
        </div>

        {!task ? (
          <EmptyState icon="ti-cash" message="Please select a finance clearance task from the dropdown above." />
        ) : (
          <>
            {/* Employee card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <InfoRow label="Employee Name" value={task.emp_name} />
                <InfoRow label="Employee No." value={task.emp_no} />
                <InfoRow label="Designation" value={task.designation} />
                <InfoRow label="Department" value={task.department} />
                <InfoRow label="ND Case ID" value={task.nd_case_id} />
                <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                  <span className="text-muted small">Overall Case Status:</span>
                  <StatusBadge status={task.status} />
                </div>
              </div>
            </div>

            {/* Loans */}
            <SectionCard index={0} title="Outstanding Loans & Advances">
              <WarningBanner type="info" message="Check outstanding amounts across all categories. Outstanding amounts must be reconciled or set for recovery." />
              {loadingLoans ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
              ) : loans.length === 0 ? (
                <p className="text-muted fst-italic mb-0">No outstanding loans or advances found on record.</p>
              ) : (
                loans.map((loan, idx) => (
                  <div key={idx} className="py-3 border-bottom">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="fw-semibold flex-grow-1">{loan.loan_type}</span>
                      <span className={`badge ${loan.status === "Active" ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"}`}>
                        {loan.status}
                      </span>
                    </div>
                    {loan.status === "Active" ? (
                      <>
                        <div className="row g-2 mb-2">
                          <div className="col-6">
                            <div className="p-2 rounded" style={{ background: "rgba(0,0,0,0.03)" }}>
                              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Sanctioned</div>
                              <div className="fw-semibold">{formatINR(loan.sanctioned_amount)}</div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-2 rounded" style={{ background: "rgba(0,0,0,0.03)" }}>
                              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Outstanding</div>
                              <div className="fw-semibold" style={{ color: "#C0282C" }}>{formatINR(loan.outstanding_amount)}</div>
                            </div>
                          </div>
                        </div>
                        <FieldLabel label="Recovery Required (₹)" required />
                        <input type="number" className="form-control form-control-sm mb-2" value={loan.recovery_required || ""} onChange={(e) => setRecovery(idx, e.target.value)} placeholder="Enter recovery amount..." />
                        <FieldLabel label="Loan Remarks" />
                        <input className="form-control form-control-sm" value={loan.remarks || ""} onChange={(e) => setLoanRemark(idx, e.target.value)} placeholder="Add loan/advance remarks..." />
                      </>
                    ) : (
                      <p className="text-muted fst-italic mb-0 small">No outstanding dues found.</p>
                    )}
                  </div>
                ))
              )}
            </SectionCard>

            {/* Recovery summary */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <h6 className="fw-semibold">Recovery Summary</h6>
                <hr className="my-2" />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold">Total Recovery Amount:</span>
                  <span className="fw-bold fs-5" style={{ color: totalRecovery > 0 ? "#C0282C" : "#049123" }}>
                    {formatINR(totalRecovery)}
                  </span>
                </div>
                {totalRecovery > 0 && (
                  <WarningBanner type="warning" message="Dues will be deducted from the employee's final settlement/gratuity." />
                )}
              </div>
            </div>

            {/* Decision */}
            <SectionCard index={1} title="Clearance Decision">
              <FieldLabel label="Select Clearance Status" required />
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div
                    className={`card border h-100 text-center ${totalRecovery > 0 ? "opacity-50" : ""}`}
                    style={{ borderRadius: 12, cursor: totalRecovery > 0 ? "not-allowed" : "pointer", borderColor: status === "Cleared" ? "#04912366" : undefined, background: status === "Cleared" ? "#04912310" : undefined }}
                    onClick={() => totalRecovery === 0 && setStatus("Cleared")}
                  >
                    <div className="card-body">
                      <i className="ti ti-circle-check" style={{ fontSize: 22, color: status === "Cleared" ? "#049123" : "#9aa0a6" }} />
                      <div className="fw-semibold mt-1" style={{ color: status === "Cleared" ? "#049123" : undefined }}>Cleared</div>
                      <small className="text-muted">No recovery outstanding</small>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div
                    className="card border h-100 text-center"
                    style={{ borderRadius: 12, cursor: "pointer", borderColor: status === "Dues Found" ? "#FF0F0F66" : undefined, background: status === "Dues Found" ? "#FF0F0F10" : undefined }}
                    onClick={() => setStatus("Dues Found")}
                  >
                    <div className="card-body">
                      <i className="ti ti-alert-circle" style={{ fontSize: 22, color: status === "Dues Found" ? "#FF0F0F" : "#9aa0a6" }} />
                      <div className="fw-semibold mt-1" style={{ color: status === "Dues Found" ? "#FF0F0F" : undefined }}>Dues Found</div>
                      <small className="text-muted">Dues mapped to recovery</small>
                    </div>
                  </div>
                </div>
              </div>
              <FieldLabel label="Overall Remarks" required={status === "Dues Found"} />
              <textarea className="form-control" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter overall financial clearance remarks..." />
            </SectionCard>

            {/* Documents */}
            <SectionCard index={2} title="Supporting Documents">
              <UploadBox hint="Upload bank receipts or clearance letters" />
            </SectionCard>

            <div className="d-flex justify-content-end bg-white border-top py-3 px-2" style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
              <button className="btn btn-primary px-4" onClick={handleSubmit} disabled={!status || submitting}>
                <i className="ti ti-send me-1" />
                Submit Finance Clearance
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinanceClearance;
